import {
  Button,
  ButtonSize,
  ButtonVariant,
  Hide,
  Show
} from "shared/components";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import { Row } from "views/applications-id-report/components/Row";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { useState } from "react";
import { Property } from "types/landlord/address";
import { PropertyGeneralForm } from "shared/app/PropertyGeneralForm";
import { SectionCard } from "shared/app/SectionCard";
import { useFormTyped } from "shared/hooks";
import { PartnerFeatures, PropertyKind } from "shared/types";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import addressApi from "api/landlord/address";
import * as yup from "yup";
import { useAuthContext } from "providers/Auth";
import { getErrorMessage } from "utils/error";

const getGeneralSectionValidationSchema = ({
  hasPropertyOwner = false
}: {
  hasPropertyOwner: boolean;
}) => {
  return yup.object().shape({
    propertyId: yup.number().required(),
    name: yup.string().required("Property name is required"),
    owner_name: hasPropertyOwner
      ? yup.string().required("Owner name is required")
      : yup.string().nullable().optional(),
    address1: yup.string().required("Street address is required"),
    address2: yup.string().optional(),
    city: yup.string().required("City is required"),
    state_or_province: yup.string().required("State is required"),
    postal_code: yup
      .string()
      .required("Zip is required")
      .min(5, "Zip code must be at least 5 characters")
      .max(10, "Zip code must be less than or equal to 10 characters"),
    country: yup.string().optional()
  });
};

interface EditGeneralFormProps {
  onClose: () => void;
  initialValues: {
    propertyId: number;
    name: string;
    kind: PropertyKind | null;
    address1: string;
    address2?: string;
    state_or_province: string;
    city: string;
    postal_code: string;
    country: string;
    owner_name?: string | null;
    description?: string;
    external_id?: string;
  };
}

const EditGeneralForm = ({ onClose, initialValues }: EditGeneralFormProps) => {
  const queryClient = useQueryClient();
  const { partnerFeatures } = useAuthContext();
  const hasPropertyOwner = partnerFeatures.includes(
    PartnerFeatures.PropertyOwner
  );
  const { register, handleChange, onSubmit } = useFormTyped(
    initialValues,
    getGeneralSectionValidationSchema({ hasPropertyOwner })
  );

  const { mutate: updateProperty, isPending } = useMutation({
    mutationFn: (values: EditGeneralFormProps["initialValues"]) => {
      return addressApi.editProperty({
        propertyId: values.propertyId,
        name: values.name,
        owner_name: values.owner_name ?? undefined,
        address: {
          address1: values.address1,
          address2: values.address2,
          state_or_province: values.state_or_province,
          city: values.city,
          postal_code: values.postal_code,
          country: values.country
        },
        description: values.description,
        external_id: values.external_id
      });
    },
    onSuccess: () => {
      toast.success("Property updated successfully");
      queryClient.invalidateQueries({
        queryKey: ["properties", String(initialValues.propertyId)]
      });
      onClose();
    },
    onError: (error) => {
      toast.error(`Failed to update property: ${getErrorMessage(error)}`);
    }
  });

  const handleSubmit = onSubmit({
    onSuccess: (values) => {
      updateProperty(values);
    },
    onError: () => {
      toast.error("Form validation failed");
    }
  });

  return (
    <PropertyGeneralForm
      propertyKindDisabled
      isDefaultProperty={!initialValues.kind}
      handleChange={handleChange}
      register={register as any}
      footer={
        <SaveCancelButtons
          onSave={(e) => handleSubmit(e)}
          onCancel={onClose}
          saveLoading={isPending}
        />
      }
    />
  );
};

const propertyKindLabel = {
  [PropertyKind.SingleFamily]: "Single family",
  [PropertyKind.MultiFamily]: "Multi family"
};

export const GeneralSection = ({ property }: { property: Property }) => {
  const { partnerFeatures } = useAuthContext();
  const { Property: PropertyLabel } = useUnitsPropertiesDictionary();
  const [isEditing, setIsEditing] = useState(false);
  const description =
    property.kind === PropertyKind.SingleFamily
      ? property.units[0]?.description
      : property.description;
  const hasPropertyOwner = partnerFeatures.includes(
    PartnerFeatures.PropertyOwner
  );

  if (isEditing)
    return (
      <EditGeneralForm
        onClose={() => setIsEditing(false)}
        initialValues={{
          propertyId: property.id,
          name: property.name,
          kind: property.kind,
          ...property.address,
          description,
          owner_name: property.owner,
          external_id: property.external_id
        }}
      />
    );

  return (
    <SectionCard
      title="General"
      headerExtra={
        <Hide if={Boolean(property.pms_id)}>
          <Button
            size={ButtonSize.SM}
            variant={ButtonVariant.BASE}
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        </Hide>
      }
    >
      <div className="space-y-4">
        <Row title={`${PropertyLabel} name`} value={property.name} />
        <Show if={hasPropertyOwner}>
          <Row title="Owner" value={property.owner} />
        </Show>
        {property.kind && propertyKindLabel[property.kind] ? (
          <Row
            title={`${PropertyLabel} type`}
            value={propertyKindLabel[property.kind]}
          />
        ) : null}
        <Row title="Street address" value={property.address.address1} />
        <Row title="Apartment, suite, etc" value={property.address.address2} />
        <Row title="State" value={property.address.state_or_province} />
        <Row title="City" value={property.address.city} />
        <Row title="Zip" value={property.address.postal_code} />
        {property.kind ? (
          <Row
            title="Description"
            value={description}
            valueClassName="w-[500px]"
          />
        ) : null}
        <Row title={`${PropertyLabel} ID`} value={property.external_id} />
      </div>
    </SectionCard>
  );
};
